<template>
  <div>
    <div class="d-flex align-center mb-3">
      <v-tabs v-model="ruleTab" style="flex: 1 1 auto">
        <v-tab>키워드</v-tab>
        <v-tab>@멘션</v-tab>
        <v-tab>도네이션</v-tab>
      </v-tabs>
      <!-- 우측: 현재 탭에 맞는 룰 생성 (도네이션 탭에선 숨김) -->
      <v-btn v-if="ruleTab !== 2" small color="primary" depressed class="ml-2 flex-shrink-0" @click="onAddCurrent">
        <v-icon left small>mdi-plus</v-icon>생성하기
      </v-btn>
    </div>
    <v-tabs-items v-model="ruleTab">
      <!-- 키워드 탭 (일반 매칭: !isMentionMode) -->
      <v-tab-item class="pt-3">
        <keyword-rule-card
          v-for="oneRule in keywordOnlyRules"
          :key="oneRule.id"
          :rule="oneRule"
          @toggle-enabled="onToggleEnabled(oneRule, $event)"
          @toggle-mention="onToggleMentionMode(oneRule, $event)"
          @change-instruction="onChangeInstruction(oneRule, $event)"
          @play="$emit('play', $event)"
          @add-word="onAddWord(oneRule, $event)"
          @remove-word="onRemoveWord(oneRule, $event)"
          @edit="onEditRule(oneRule)"
          @delete="onDeleteKeywordRule(oneRule)"
        ></keyword-rule-card>
      </v-tab-item>

      <!-- @멘션 탭 (멘션 매칭: isMentionMode). @펫이름으로 시작하는 채팅에만 반응 -->
      <v-tab-item class="pt-3">
        <keyword-rule-card
          v-for="oneRule in mentionRules"
          :key="oneRule.id"
          :rule="oneRule"
          @toggle-enabled="onToggleEnabled(oneRule, $event)"
          @toggle-mention="onToggleMentionMode(oneRule, $event)"
          @change-instruction="onChangeInstruction(oneRule, $event)"
          @play="$emit('play', $event)"
          @add-word="onAddWord(oneRule, $event)"
          @remove-word="onRemoveWord(oneRule, $event)"
          @edit="onEditRule(oneRule)"
          @delete="onDeleteKeywordRule(oneRule)"
        ></keyword-rule-card>
      </v-tab-item>

      <!-- 도네이션 탭 -->
      <v-tab-item class="pt-3">
        <div class="rule__title">
          <StyledKeyword> 도네이션 감사 </StyledKeyword>
          <RuleEnableSwitch
            class="ml-3"
            :value="rules.donationEnabled"
            @change="onChangeEnableDonation"
          ></RuleEnableSwitch>
        </div>
        <div class="rule__body">
          <DonationRuleFragment
            :items="rules.donationRules"
            @changeItem="onChangeDonationItem"
            @play="$emit('play', $event)"
          ></DonationRuleFragment>
        </div>
      </v-tab-item>
    </v-tabs-items>

    <!-- 생성 다이얼로그: 리액션 선택 + 키워드(1개 이상). 같은 모드에 같은 리액션 룰 있으면 키워드만 머지 -->
    <v-dialog v-model="createDialog" max-width="440">
      <v-card class="pa-4">
        <div class="text-subtitle-1 font-weight-bold mb-3">{{ dialogTitle }}</div>

        <div class="text-caption grey--text mb-1">제목</div>
        <app-input v-model="dialogLabel" placeholder="이름없음" class="mb-3"></app-input>

        <div class="text-caption grey--text mb-1">리액션</div>
        <InstructionSelector v-model="createReactionId" hide-play hide-details dense class="mb-3"></InstructionSelector>

        <div class="text-caption grey--text mb-1">키워드 (하나 이상)</div>
        <KeywordInput hide-details dense @add="onDialogAddWord"></KeywordInput>
        <div class="mt-2">
          <KeywordChips :keywords="createKeywords" @clickRemove="onDialogRemoveWord"></KeywordChips>
        </div>

        <div class="d-flex justify-end mt-4" style="gap: 8px">
          <v-btn text @click="createDialog = false">취소</v-btn>
          <v-btn color="primary" depressed :disabled="!canCreate" @click="onDialogConfirm">
            {{ dialogMode === "edit" ? "저장" : "생성" }}
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- 키워드(태그) 삭제 확인 -->
    <v-dialog v-model="removeDialog" max-width="360">
      <v-card class="pa-4">
        <div class="text-subtitle-1 font-weight-bold mb-1">키워드 삭제</div>
        <div class="text-body-2 mb-4">
          <b>{{ pendingRemove && pendingRemove.word }}</b> 키워드를 삭제할까요?
        </div>
        <div class="d-flex justify-end" style="gap: 8px">
          <v-btn text @click="removeDialog = false">취소</v-btn>
          <v-btn color="error" depressed @click="confirmRemoveWord">삭제</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import SupabaseApi from "@/api/SupabaseApi";
import { getVirtupetPubSocket } from "@/domain/stream/pub/VirtupetPubSocket";
import RuleEnableSwitch from "../components/RuleEnableSwitch.vue";
import KeywordRuleCard from "../c/KeywordRuleCard.vue";
import KeywordInput from "../components/KeywordInput.vue";
import KeywordChips from "../components/KeywordChips.vue";
import DonationRuleFragment from "./DonationRuleFragment.vue";

// 방송 룰 편집기(키워드/도네이션 탭). LiveRemotePage에서 추출 —
//  룰 로드(owner 중심 정규화 테이블) + CRUD + 오버레이 push(펫 pub 소켓)를 자족적으로 담는다.
//  @play(reactionId): 미리보기 재생 요청을 부모로 올림(호스트가 자기 모델로 실행).
export default {
  name: "BroadcastRuleEditor",
  components: { RuleEnableSwitch, KeywordRuleCard, KeywordInput, KeywordChips, DonationRuleFragment },
  data() {
    return {
      ruleTab: 0, // 0=키워드, 1=@멘션, 2=도네이션
      rules: {
        keywordRules: [],
        donationRules: [],
        donationEnabled: true,
      },
      // 생성/수정 다이얼로그
      createDialog: false,
      dialogMode: "create", // 'create' | 'edit'
      editRuleId: null, // edit 시 대상 룰 id
      dialogLabel: "", // 제목(라벨)
      createMentionMode: false,
      createReactionId: null,
      createKeywords: [],
      // 키워드 삭제 확인 다이얼로그
      removeDialog: false,
      pendingRemove: null, // { rule, word }
    };
  },
  async created() {
    // 룰 에디터 셀렉터용 리액션 목록(reaction 테이블: system + 내것)
    const reactions = await SupabaseApi.Reaction.fetchAll();
    this.$store.commit("__setReactions", SupabaseApi.Reaction.toMaps(reactions));

    // 룰 로드 (owner 중심 정규화 테이블)
    const [keywordRules, donationRules, ubs] = await Promise.all([
      SupabaseApi.ReactionKeywordRule.fetchMine(),
      SupabaseApi.ReactionDonationRule.fetchMine(),
      SupabaseApi.UserBroadcastSetting.fetchMine(),
    ]);
    this.rules.keywordRules = keywordRules;
    this.rules.donationRules = donationRules;
    this.rules.donationEnabled = ubs.donationEnabled;
  },
  async mounted() {
    // 오버레이 push용 펫 pub 소켓(공유 인스턴스). 새 오버레이 접속 시 현재 룰 push(콜드스타트 RPC 보완).
    try {
      const petId = await SupabaseApi.UserStatus.fetchSelectedPetId();
      this._socket = getVirtupetPubSocket(petId).socket;
      this._onNotify = (data) => {
        if (data && data.name === "NEW_LIVE_MODEL_CONNECTED") this._pushRules();
      };
      this._socket.on("notifyMessage", this._onNotify);
    } catch (error) {
      console.error(error);
    }
  },
  beforeDestroy() {
    if (this._socket && this._onNotify) this._socket.off("notifyMessage", this._onNotify);
  },
  watch: {
    // 키워드룰 변경(생성·수정·삭제·단어 추가/삭제·토글) 즉시 부모로 전파 → 워드클라우드 등 실시간 반영
    "rules.keywordRules": {
      deep: true,
      handler(rules) {
        this.$emit("keyword-rules-changed", rules);
      },
    },
  },
  computed: {
    // 키워드룰을 모드로 분리(탭). isMentionMode=false → 키워드 탭, true → @멘션 탭
    keywordOnlyRules() {
      return (this.rules.keywordRules || []).filter((r) => !r.isMentionMode);
    },
    mentionRules() {
      return (this.rules.keywordRules || []).filter((r) => r.isMentionMode);
    },
    // 생성 가능: 리액션 선택 + 키워드 1개 이상
    canCreate() {
      return !!this.createReactionId && this.createKeywords.length > 0;
    },
    dialogTitle() {
      const kind = this.createMentionMode ? "@멘션 룰" : "키워드 룰";
      return this.dialogMode === "edit" ? `${kind} 수정` : `${kind} 생성`;
    },
  },
  methods: {
    // 채팅 룰 CRUD (reaction_keyword_rule) — 변경마다 DB write + 오버레이 push
    async _persistKeywordRule(rule, patch) {
      Object.assign(rule, patch); // 로컬 낙관 반영
      await SupabaseApi.ReactionKeywordRule.update(rule.id, patch);
      this._pushRules();
    },
    async onToggleEnabled(rule, isEnabled) {
      await this._persistKeywordRule(rule, { isEnabled: !!isEnabled });
    },
    async onToggleMentionMode(rule, isMentionMode) {
      await this._persistKeywordRule(rule, { isMentionMode: !!isMentionMode });
    },
    async onChangeInstruction(rule, reactionId) {
      await this._persistKeywordRule(rule, { reactionId });
    },
    async onAddWord(rule, word) {
      if (!word || rule.keywords.includes(word)) return;
      await this._persistKeywordRule(rule, { keywords: [...rule.keywords, word] });
    },
    // 키워드 삭제는 확인 다이얼로그 경유
    onRemoveWord(rule, word) {
      this.pendingRemove = { rule, word };
      this.removeDialog = true;
    },
    async confirmRemoveWord() {
      const p = this.pendingRemove;
      this.removeDialog = false;
      if (!p) return;
      await this._persistKeywordRule(p.rule, { keywords: p.rule.keywords.filter((w) => w !== p.word) });
      this.pendingRemove = null;
    },
    // 우측 생성하기 → 생성 다이얼로그 오픈 (현재 탭 모드로, 빈 값)
    onAddCurrent() {
      this.dialogMode = "create";
      this.editRuleId = null;
      this.dialogLabel = "";
      this.createMentionMode = this.ruleTab === 1;
      this.createReactionId = null;
      this.createKeywords = [];
      this.createDialog = true;
    },
    // 더보기 → 수정하기 → 현재값을 채운 다이얼로그(제목/리액션/키워드 편집)
    onEditRule(rule) {
      this.dialogMode = "edit";
      this.editRuleId = rule.id;
      this.dialogLabel = rule.label || "";
      this.createMentionMode = !!rule.isMentionMode;
      this.createReactionId = rule.reactionId;
      this.createKeywords = [...(rule.keywords || [])];
      this.createDialog = true;
    },
    onDialogAddWord(word) {
      const w = (word || "").trim();
      if (!w || this.createKeywords.includes(w)) return;
      this.createKeywords = [...this.createKeywords, w];
    },
    onDialogRemoveWord(word) {
      this.createKeywords = this.createKeywords.filter((w) => w !== word);
    },
    // 확정(생성/수정). 라벨은 비면 "이름없음".
    async onDialogConfirm() {
      const reactionId = this.createReactionId;
      const words = this.createKeywords;
      const isMentionMode = this.createMentionMode;
      if (!reactionId || !words.length) return; // 키워드 없이는 불가
      const label = (this.dialogLabel || "").trim() || "이름없음";

      // 수정: 대상 룰 업데이트 (제목/리액션/키워드)
      if (this.dialogMode === "edit") {
        const rule = this.rules.keywordRules.find((r) => r.id === this.editRuleId);
        if (rule) await this._persistKeywordRule(rule, { label, reactionId, keywords: words });
        this.createDialog = false;
        return;
      }

      // 생성: 같은 모드 + 같은 리액션 룰이 있으면 키워드 머지, 없으면 새로 생성
      const existing = this.rules.keywordRules.find(
        (r) => r.reactionId === reactionId && !!r.isMentionMode === isMentionMode,
      );
      if (existing) {
        const merged = [...new Set([...existing.keywords, ...words])];
        await this._persistKeywordRule(existing, { keywords: merged });
      } else {
        const id = await SupabaseApi.ReactionKeywordRule.create({ label, isMentionMode, reactionId, keywords: words });
        this.rules.keywordRules.push({ id, isMentionMode, keywords: words, reactionId, isEnabled: true, label });
        this._pushRules();
      }
      this.createDialog = false;
    },
    async onDeleteKeywordRule(rule) {
      const isYes = window.confirm("정말 삭제하시겠습니까?") || false;
      if (!isYes) return;

      await SupabaseApi.ReactionKeywordRule.remove(rule.id);
      this.rules.keywordRules = this.rules.keywordRules.filter((r) => r.id !== rule.id);
      this._pushRules();
    },
    async onChangeEnableDonation(value) {
      this.rules.donationEnabled = !!value;
      await SupabaseApi.UserBroadcastSetting.upsert({ donationEnabled: this.rules.donationEnabled });
      this._pushRules();
    },
    async onChangeDonationItem(id, reactionId) {
      const item = this.rules.donationRules.find((d) => d.id === id);
      if (item) item.reactionId = reactionId;
      await SupabaseApi.ReactionDonationRule.update(id, { reactionId });
      this._pushRules();
    },
    // 오버레이로 룰 전체 push (새 shape). 소켓 없으면 무시(콜드스타트 시 RPC로 로드).
    _pushRules() {
      if (!this._socket) return;
      this._socket.emit("requestCommand", {
        method: "updateRules",
        to: "liveModel",
        body: {
          keywordRules: this.rules.keywordRules,
          donationRules: this.rules.donationRules,
          donationEnabled: this.rules.donationEnabled,
        },
        time: Date.now(),
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.rule__title {
  display: flex;
  align-items: center;

  margin-top: 8px;
  margin-bottom: 8px;
}

.rule__body {
  border-radius: 10px;
  padding: 16px;
  box-shadow: 0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14),
    0px 1px 3px 0px rgba(0, 0, 0, 0.12) !important;

  margin-bottom: 30px;
}
</style>
